import { logService } from '@/common/services/logger';
import { axiosErrorParser, errorParser } from '@/common/services/error-parser';
import { isAxiosError } from 'axios';
import { storeToRefs } from 'pinia';

import { TNotificationTypeEnum, useNotificationsStore } from '@/common/stores/notifications';
import i18n from '@/plugins/i18n';
import { useDmAcceptorsStore } from './dm/dmAcceptorsStore';
import type { AcceptorStateChangeEvent } from './types';

const handleAcceptorsChangeStateError = (
  acceptMoney: boolean,
  event: AcceptorStateChangeEvent,
  err: unknown,
) => {
  const action = acceptMoney ? 'START' : 'STOP';
  const error = isAxiosError(err)
    ? axiosErrorParser.parseUpstream(err)
    : errorParser.parseUpstream(err);

  logService.error(`[AcceptorsService] Failed to send message to ${action} acceptors.`, {
    code: `T_ACCEPTORS_${action}_ERROR`,
    details: {
      acceptMoney,
    },
    ...error,
  });

  event.reject(err);
};

const handleNarAcceptorsChangeStateSuccess = (
  acceptMoney: boolean,
  event: AcceptorStateChangeEvent,
) => {
  const action = acceptMoney ? 'START' : 'STOP';

  logService.info(`[AcceptorsService] Message to ${action} acceptors successfully sent.`, {
    code: 'T_ACCEPTORS_CHANGE_ACTION_SUCCESS',
    details: {
      acceptMoney,
    },
  });

  event.resolve();
};

const showDmAcceptorStartBlockerErrors = () => {
  const { acceptorStartBlockers } = storeToRefs(useDmAcceptorsStore());
  const notificationsStore = useNotificationsStore();
  const { t } = i18n.global;

  const devices: Array<keyof typeof acceptorStartBlockers.value> = ['bill', 'coin'];

  // close previous acceptors start blockers notifications so there are no possible duplicates
  notificationsStore.closeNotificationWithId('acceptors_start_blocker_bill');
  notificationsStore.closeNotificationWithId('acceptors_start_blocker_coin');

  devices.forEach((device) => {
    const blockers = acceptorStartBlockers.value[device];

    if (blockers.length) {
      notificationsStore.show({
        id: `acceptors_start_blocker_${device}`,
        message: blockers.map((blockerKey) => t(`acceptors_start_blocker_${blockerKey}`)),
        type: TNotificationTypeEnum.error,
        title: t(`acceptors_start_blocker_${device}_title`),
        delay: null,
        closeDisabled: true,
      });
    }
  });
};

export {
  handleAcceptorsChangeStateError,
  handleNarAcceptorsChangeStateSuccess,
  showDmAcceptorStartBlockerErrors,
};
